<script setup lang="ts">
import { watch, nextTick } from "vue";

import type { Configurator } from "../libs/configurator/Configurator";

const props = defineProps<{
    cfg: Configurator;
    productId: 0 | 1; // 0=primary, 1=secondary
}>();

const emit = defineEmits<(e: "onChange") => void>();

function normalize(code: string) {
    const upper = code.toUpperCase().trim();
    if (upper === "ZT") { return "ZB"; }
    if (upper === "ZQ") { return "ZP"; }
    return upper;
}

// "90487/37zb" -> ["37", "ZB"]
function extractAttributesFromMastercode(mastercode: string = ""): string[] {
    console.log("Extracting attributes from mastercode:", mastercode);
    const parts = mastercode.split("/");
    if (parts.length !== 2) { return []; }
    const tail = parts[1].toUpperCase().trim();
    const chunks = tail.match(/.{1,2}/g)?.filter((c) => c.length === 2); // split in chunks of 2 characters
    return chunks
        ?.filter((c) => props.cfg.materialAttributes.some((attr) => attr.code === normalize(c)))
        .map(normalize) || [];
}

function materialValuesFor(attributeCode: string): string[] {
    return props.cfg.materialAttributes.find((a) => a.code === attributeCode)?.values || [];
}

function defaultIfMissing(part: any, attribute: string, index: number) {
    if (getSelectedValue(part, attribute, index) === "") {
        return;
    }

    const options = materialValuesFor(attribute);
    if (options.length > 0) {
        props.cfg.setAttribute(props.productId, part.mastercode, attribute, index, options[0]);
    }
}

function getSelectedValue(part: any, attribute: string, index: number): string {
    const v = part.attributes?.[attribute];
    if (Array.isArray(v)) {
        return v[index]?.value ?? "";
    }
    if (typeof v === "string") {
        return index === 0 ? v : "";
    }
    return "";
}

function onAttributeChange(mastercode: string, attribute: string, index: number, value: string, productId: 0 | 1): void {
    props.cfg.setAttribute(productId, mastercode, attribute, index, value);
    emit("onChange");
}

watch(() => [props.productId, JSON.stringify(props.cfg.primary), JSON.stringify(props.cfg.secondary ?? null)], async () => {
    await nextTick();
    const parts = props.productId === 0 ? props.cfg.primary.parts : props.cfg.secondary?.parts ?? [];
    for (const part of parts) {
        const mc = part.code_master;
        console.log("Checking part for defaults:", mc);
        extractAttributesFromMastercode(mc).forEach((attr, i) => {
            defaultIfMissing(part, attr, i);
        });
    }
    emit("onChange");
}, { immediate: true });
</script>

<template>
    <div class="materials-grid">
        <div
            v-for="part of (productId === 0 ? cfg.primary.parts : cfg.secondary?.parts || [])"
            :key="part.code_master"
            class="mat-card"
            style="margin: 8px 0;"
        >
            <strong>{{ part.code_master }}</strong>
            
            <template
                v-for="(attr, index) of extractAttributesFromMastercode(part.code_master)"
                :key="attr + index"
            >
                <div class="attr-row">
                    <label style="margin-left: 8px;">{{ attr }}</label>
                    <select
                        :value="part[attr] || ''"
                        style="margin-left: 4px; padding: 2px 4px;"
                        @change="(e) => onAttributeChange(part.code_master, attr, index, (e.target as HTMLSelectElement).value, productId)"
                    >
                        <option
                            v-for="v of materialValuesFor(attr)"
                            :key="v"
                            :value="v"
                        >
                            {{ v }}
                        </option>
                    </select>
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped>
/* Cards still lay out side-by-side */
.materials-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
}

/* Each card is a single no-wrap horizontal row; scroll if overflow */
.mat-card {
  flex: 0 0 auto;        /* don’t grow/shrink, keep natural size */
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;

  white-space: nowrap;     /* keep everything on one line */
  overflow-x: auto;        /* allow horizontal scrolling if it’s too long */
  overflow-y: hidden;
}

/* Mastercode label */
.mc-label {
  font-weight: 600;
  font-size: 13px;
  color: #111827;
  flex: 0 0 auto;
}

/* Attribute “chip”: label + select inline */
.attr-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;       /* spacing between chips */
  flex: 0 0 auto;
}

.attr-chip label {
  font-size: 12px;
  color: #374151;
}

.attr-chip select {
  padding: 2px 6px;
  min-height: 24px;
  font-size: 12px;
}
</style>
